/**
 * The Hondo V2 UI kit. Small, plain components on the shared Maroon and Cool Gray tokens. Every
 * visible string passed in should already be translated with t(). Field-friendly by default:
 * 44px minimum targets, 56px for primary field actions.
 */
import {
  Suspense, createContext, forwardRef, useCallback, useContext, useEffect, useId, useRef, useState, useSyncExternalStore,
  type ButtonHTMLAttributes, type InputHTMLAttributes, type KeyboardEvent as ReactKeyboardEvent, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes,
} from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router";
import type { Badge as BadgeDef, Fact, Tone } from "../../contract";
import { localizeDates, t } from "../lib/i18n";
import { iconFor } from "../registry";

const cx = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(" ");

export function Icon({ name, size = 20, className, label }: { name: string; size?: number; className?: string; label?: string }) {
  const C = iconFor(name);
  return <C size={size} className={cx("icon", className)} aria-hidden={label ? undefined : true} aria-label={label} strokeWidth={2} />;
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "ghost" | "danger" | "good";
  size?: "sm" | "md" | "lg";
  icon?: string;
  iconRight?: string;
  busy?: boolean;
  block?: boolean;
  to?: string;
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button({ variant = "secondary", size = "md", icon, iconRight, busy, block, to, children, className, disabled, ...rest }, ref) {
  const cls = cx("btn", `btn-${variant}`, `btn-${size}`, block && "btn-block", className);
  const content = <>{busy ? <Icon name="spinner" className="spin" size={size === "lg" ? 22 : 18} /> : icon && <Icon name={icon} size={size === "lg" ? 22 : 18} />}{children && <span>{children}</span>}{iconRight && <Icon name={iconRight} size={18} />}</>;
  if (to) return <Link to={to} className={cls}>{content}</Link>;
  return <button ref={ref} type="button" className={cls} disabled={disabled || busy} aria-busy={busy || undefined} {...rest}>{content}</button>;
});

export function IconButton({ icon, label, className, size = 20, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { icon: string; label: string; size?: number }) {
  return <button type="button" className={cx("icon-btn", className)} aria-label={label} title={label} {...rest}><Icon name={icon} size={size} /></button>;
}

export function Card({ children, className, tone, onClick, to, padded = true, state }: { children: ReactNode; className?: string; tone?: Tone; onClick?: () => void; to?: string; padded?: boolean; state?: unknown }) {
  const cls = cx("card", padded && "card-pad", tone && `tone-${tone}`, (onClick || to) && "card-link", className);
  if (to) return <Link to={to} state={state} className={cls}>{children}</Link>;
  if (onClick) return <button type="button" className={cls} onClick={onClick}>{children}</button>;
  return <div className={cls}>{children}</div>;
}

export function Section({ title, action, count, hint, children, className }: { title?: ReactNode; action?: ReactNode; count?: number; hint?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={cx("section", className)}>
      {(title || action) && (
        <div className="section-head">
          <h2>{title}{count !== undefined && <span className="count">{count}</span>}</h2>
          {action}
        </div>
      )}
      {hint && <p className="section-hint">{hint}</p>}
      {children}
    </section>
  );
}

/** Where a page was opened from, when the link said so (see `fromHere`). The back link returns there. */
export interface ReturnTo { to: string; label: string }
export function useReturnTo(): ReturnTo | null {
  const state = useLocation().state as { from?: ReturnTo } | null;
  return state?.from && typeof state.from.to === "string" && typeof state.from.label === "string" ? state.from : null;
}

/** Router state that makes the next page's back link return here: `<Link to=… state={useFromHere(job.title)}>`. */
export function useFromHere(label: string): { from: ReturnTo } {
  const loc = useLocation();
  return { from: { to: `${loc.pathname}${loc.search}`, label } };
}

export function PageHeader({ title, subtitle, back, actions, eyebrow, children }: { title: ReactNode; subtitle?: ReactNode; back?: { to: string; label: string }; actions?: ReactNode; eyebrow?: ReactNode; children?: ReactNode }) {
  // Opened from a job, search or Today: go back there instead of the page's usual parent.
  const from = useReturnTo();
  const target = from ?? back;
  return (
    <header className="page-header">
      {target && <Link to={target.to} className="back-link"><Icon name="back" size={18} /><span>{target.label}</span></Link>}
      {eyebrow && <div className="eyebrow">{eyebrow}</div>}
      <div className="page-header-row">
        <div className="page-header-text">
          <h1>{title}</h1>
          {subtitle && <p className="subtitle">{subtitle}</p>}
        </div>
        {actions && <div className="page-actions">{actions}</div>}
      </div>
      {children}
    </header>
  );
}

export function List({ children, className }: { children: ReactNode; className?: string }) {
  return <ul className={cx("list", className)}>{children}</ul>;
}

export function Row({ icon, tone, leading, title, meta, trailing, to, onClick, selected, className, state }: {
  icon?: string; tone?: Tone; leading?: ReactNode; title: ReactNode; meta?: ReactNode; trailing?: ReactNode; to?: string; onClick?: () => void; selected?: boolean; className?: string;
  /** Router state for the link, e.g. `fromHere(label)` so the next page's back link returns here. */
  state?: unknown;
}) {
  const inner = (
    <>
      {leading ?? (icon && <span className={cx("row-icon", tone && `tone-${tone}`)}><Icon name={icon} size={18} /></span>)}
      <span className="row-main"><span className="row-title">{title}</span>{meta && <span className="row-meta">{meta}</span>}</span>
      {trailing && <span className="row-trailing">{trailing}</span>}
      {(to || onClick) && !trailing && <Icon name="chevron" size={18} className="row-chevron" />}
    </>
  );
  return (
    <li className={cx("row", selected && "row-selected", className)}>
      {to ? <Link to={to} state={state} className="row-inner">{inner}</Link> : onClick ? <button type="button" className="row-inner" onClick={onClick}>{inner}</button> : <div className="row-inner">{inner}</div>}
    </li>
  );
}

export function Chip({ tone = "neutral", icon, children, onRemove, title, removeLabel }: { tone?: Tone; icon?: string; children: ReactNode; onRemove?: () => void; title?: string; removeLabel?: string }) {
  return (
    <span className={cx("chip", `tone-${tone}`)} title={title}>
      {icon && <Icon name={icon} size={14} />}
      <span>{children}</span>
      {onRemove && <button type="button" className="chip-x" onClick={onRemove} aria-label={removeLabel ?? t("Remove")}><Icon name="close" size={14} /></button>}
    </span>
  );
}

export function Badges({ badges, max = 4 }: { badges: BadgeDef[]; max?: number }) {
  if (!badges.length) return null;
  return <span className="badges">{badges.slice(0, max).map((b, i) => <Chip key={i} tone={b.tone} icon={b.icon}>{t(b.label.text, b.label.values)}</Chip>)}</span>;
}

/** An ear tag, set in mono so 0 and O, 1 and I never get confused. */
export function TagLabel({ tag, size = "md", muted }: { tag: string | null | undefined; size?: "sm" | "md" | "lg" | "xl"; muted?: boolean }) {
  if (!tag) return null;
  return <span className={cx("ear-tag", `ear-tag-${size}`, muted && "ear-tag-muted")}>{tag}</span>;
}

export function Stat({ label, value, hint, tone, to }: { label: ReactNode; value: ReactNode; hint?: ReactNode; tone?: Tone; to?: string }) {
  const body = <><span className="stat-value">{value}</span><span className="stat-label">{label}</span>{hint && <span className="stat-hint">{hint}</span>}</>;
  return to ? <Link to={to} className={cx("stat", tone && `tone-${tone}`, "stat-link")}>{body}</Link> : <div className={cx("stat", tone && `tone-${tone}`)}>{body}</div>;
}

export function Facts({ facts }: { facts: Fact[] }) {
  if (!facts.length) return null;
  return (
    <dl className="facts">
      {facts.map((f, i) => (
        <div key={i} className={cx("fact", f.tone && `tone-${f.tone}`)}>
          <dt>{f.icon && <Icon name={f.icon} size={14} />}{t(f.label)}</dt>
          <dd>{typeof f.value === "string" ? localizeDates(f.value) : t(f.value.text, f.value.values)}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Empty({ icon = "sparkle", title, body, action }: { icon?: string; title: ReactNode; body?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      <span className="empty-icon"><Icon name={icon} size={26} /></span>
      <p className="empty-title">{title}</p>
      {body && <p className="empty-body">{body}</p>}
      {action}
    </div>
  );
}

export function Spinner({ label }: { label?: string }) {
  return <span className="spinner" role="status"><Icon name="spinner" className="spin" size={18} />{label && <span>{label}</span>}</span>;
}

export function Loading() {
  return <div className="loading"><Spinner label={t("Loading…")} /></div>;
}

export function ErrorNote({ error, retry }: { error: unknown; retry?: () => void }) {
  return (
    <div className="error-note" role="alert">
      <Icon name="warn" size={18} />
      <span>{error instanceof Error ? error.message : t("Something went wrong on our side. Try again.")}</span>
      {retry && <Button size="sm" variant="ghost" onClick={retry}>{t("Try again")}</Button>}
    </div>
  );
}

export function Progress({ done, total, exceptions = 0, label }: { done: number; total: number; exceptions?: number; label?: string }) {
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <div className="progress" role="progressbar" aria-valuenow={done} aria-valuemin={0} aria-valuemax={total} aria-label={label}>
      <div className="progress-bar"><span style={{ width: `${pct}%` }} /></div>
      <span className="progress-text">{t("{{done}} of {{total}}", { done, total })}{exceptions ? ` · ${t("{{count}} need a look", { count: exceptions })}` : ""}</span>
    </div>
  );
}

// ---------------------------------------------------------------- tabs

export interface TabDef { id: string; label: string; count?: number; to?: string }

export function Tabs({ tabs, value, onChange, className }: { tabs: TabDef[]; value: string; onChange?: (id: string) => void; className?: string }) {
  // Arrow keys move between tabs, as screen reader users expect from a tab list.
  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    const at = tabs.findIndex((x) => x.id === value);
    const next = tabs[(at + (e.key === "ArrowRight" ? 1 : -1) + tabs.length) % tabs.length];
    if (!next) return;
    e.preventDefault();
    const list = e.currentTarget;
    onChange?.(next.id);
    requestAnimationFrame(() => list.querySelector<HTMLElement>(`[data-tab="${CSS.escape(next.id)}"]`)?.focus());
  };
  return (
    <div className={cx("tabs", className)} role="tablist" onKeyDown={onChange ? onKeyDown : undefined}>
      {tabs.map((tab) => {
        const active = tab.id === value;
        const content = <>{tab.label}{tab.count !== undefined && tab.count > 0 && <span className="count">{tab.count}</span>}</>;
        return tab.to
          ? <Link key={tab.id} to={tab.to} role="tab" data-tab={tab.id} aria-selected={active} className={cx("tab", active && "tab-active")} replace>{content}</Link>
          : <button key={tab.id} type="button" role="tab" data-tab={tab.id} aria-selected={active} tabIndex={active ? 0 : -1} className={cx("tab", active && "tab-active")} onClick={() => onChange?.(tab.id)}>{content}</button>;
      })}
    </div>
  );
}

export function Segmented<T extends string>({ options, value, onChange, label }: { options: Array<{ value: T; label: string }>; value: T; onChange: (v: T) => void; label: string }) {
  return (
    <div className="segmented" role="radiogroup" aria-label={label}>
      {options.map((o) => <button key={o.value} type="button" role="radio" aria-checked={o.value === value} className={cx("seg", o.value === value && "seg-active")} onClick={() => onChange(o.value)}>{o.label}</button>)}
    </div>
  );
}

// ---------------------------------------------------------------- overlays

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), audio[controls]';

/** Keep Tab and Shift+Tab inside a dialog while it is open. */
export function useFocusTrap(ref: { current: HTMLElement | null }, active: boolean) {
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Tab" || !ref.current) return;
      const dialogs = document.querySelectorAll("[aria-modal='true']");
      if (dialogs.length && dialogs[dialogs.length - 1] !== ref.current) return;
      const items = [...ref.current.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => !el.hidden && el.getClientRects().length > 0);
      if (!items.length) { e.preventDefault(); ref.current.focus(); return; }
      const first = items[0];
      const last = items[items.length - 1];
      const inside = ref.current.contains(document.activeElement);
      if (e.shiftKey && (document.activeElement === first || !inside)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && (document.activeElement === last || !inside)) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [ref, active]);
}

let openSheets = 0;
/** True while any sheet is open, so page shortcuts (J/K, ⌘K) stand aside. */
export const isSheetOpen = () => openSheets > 0;

export function Sheet({ open, onClose, title, children, footer, size = "md", labelledBy, initialFocus, className }: {
  open: boolean; onClose: () => void; title?: ReactNode; children: ReactNode; footer?: ReactNode; size?: "sm" | "md" | "lg" | "full"; labelledBy?: string;
  /** CSS selector inside the sheet to focus first. Defaults to the first field or button in the body. */
  initialFocus?: string;
  className?: string;
}) {
  const id = useId();
  const ref = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useFocusTrap(ref, open);
  useEffect(() => {
    if (!open) return;
    openSheets++;
    const prev = document.activeElement as HTMLElement | null;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      // Only the innermost open sheet closes.
      const dialogs = document.querySelectorAll(".sheet");
      if (dialogs[dialogs.length - 1] !== ref.current) return;
      e.preventDefault();
      closeRef.current();
    };
    document.addEventListener("keydown", onKey);
    document.body.classList.add("sheet-open");
    const frame = requestAnimationFrame(() => {
      const target = (initialFocus ? ref.current?.querySelector<HTMLElement>(initialFocus) : null)
        ?? ref.current?.querySelector<HTMLElement>(".sheet-body [autofocus], .sheet-body input:not([type='hidden']), .sheet-body textarea, .sheet-body select, .sheet-body button, .sheet-body a[href]")
        ?? ref.current;
      target?.focus();
    });
    return () => {
      openSheets--;
      cancelAnimationFrame(frame);
      document.removeEventListener("keydown", onKey);
      if (!openSheets) document.body.classList.remove("sheet-open");
      if (prev && document.contains(prev)) prev.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  if (!open) return null;
  return createPortal(
    <div className="sheet-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={ref} tabIndex={-1} className={cx("sheet", `sheet-${size}`, className)} role="dialog" aria-modal="true" aria-labelledby={labelledBy ?? (title ? id : undefined)}>
        {title && (
          <div className="sheet-head">
            <h2 id={id}>{title}</h2>
            <IconButton icon="close" label={t("Close")} onClick={onClose} className="sheet-close" />
          </div>
        )}
        <div className="sheet-body"><Suspense fallback={<Loading />}>{children}</Suspense></div>
        {footer && <div className="sheet-foot">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

export function Menu({ label, icon = "more", items, align = "end", variant = "ghost", children }: {
  label: string; icon?: string; align?: "start" | "end"; variant?: ButtonProps["variant"]; children?: ReactNode;
  items: Array<{ label: string; icon?: string; onSelect?: () => void; to?: string; danger?: boolean; disabled?: boolean } | "divider">;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const close = (refocus = true) => { setOpen(false); if (refocus) trigger.current?.focus(); };
  useEffect(() => {
    if (!open) return;
    const outside = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const keys = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); setOpen(false); trigger.current?.focus(); return; }
      const entries = [...(pop.current?.querySelectorAll<HTMLElement>(".menu-item:not([disabled])") ?? [])];
      if (!entries.length) return;
      const at = entries.indexOf(document.activeElement as HTMLElement);
      if (e.key === "ArrowDown") { e.preventDefault(); entries[(at + 1) % entries.length].focus(); }
      else if (e.key === "ArrowUp") { e.preventDefault(); entries[(at - 1 + entries.length) % entries.length].focus(); }
      else if (e.key === "Home") { e.preventDefault(); entries[0].focus(); }
      else if (e.key === "End") { e.preventDefault(); entries[entries.length - 1].focus(); }
      else if (e.key === "Tab") setOpen(false);
    };
    document.addEventListener("mousedown", outside);
    document.addEventListener("keydown", keys, true);
    const frame = requestAnimationFrame(() => pop.current?.querySelector<HTMLElement>(".menu-item:not([disabled])")?.focus());
    return () => { cancelAnimationFrame(frame); document.removeEventListener("mousedown", outside); document.removeEventListener("keydown", keys, true); };
  }, [open]);
  return (
    <div className="menu" ref={ref}>
      {children
        ? <Button ref={trigger} variant={variant} iconRight="chevronDown" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-haspopup="menu">{children}</Button>
        : <button ref={trigger} type="button" className="icon-btn" aria-label={label} title={label} onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-haspopup="menu"><Icon name={icon} size={20} /></button>}
      {open && (
        <div ref={pop} className={cx("menu-pop", `menu-${align}`)} role="menu" aria-label={label}>
          {items.map((item, i) => item === "divider" ? <hr key={i} /> : item.to
            ? <Link key={i} to={item.to} role="menuitem" className={cx("menu-item", item.danger && "menu-danger")} onClick={() => close(false)}>{item.icon && <Icon name={item.icon} size={16} />}{item.label}</Link>
            : <button key={i} type="button" role="menuitem" disabled={item.disabled} className={cx("menu-item", item.danger && "menu-danger")} onClick={() => { close(); item.onSelect?.(); }}>{item.icon && <Icon name={item.icon} size={16} />}{item.label}</button>)}
        </div>
      )}
    </div>
  );
}

export function Disclosure({ label, children, defaultOpen = false, count }: { label: ReactNode; children: ReactNode; defaultOpen?: boolean; count?: number }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={cx("disclosure", open && "disclosure-open")}>
      <button type="button" className="disclosure-toggle" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <Icon name="chevron" size={16} className="disclosure-chevron" />{label}{count !== undefined && count > 0 && <span className="count">{count}</span>}
      </button>
      {open && <div className="disclosure-body">{children}</div>}
    </div>
  );
}

// ---------------------------------------------------------------- form controls

export function Field({ label, hint, error, children, htmlFor, optional, inline }: { label: ReactNode; hint?: ReactNode; error?: ReactNode; children: ReactNode; htmlFor?: string; optional?: boolean; inline?: boolean }) {
  return (
    <div className={cx("field", !!error && "field-error", inline && "field-inline")}>
      <label htmlFor={htmlFor} className="field-label">{label}{optional && <span className="optional">{t("optional")}</span>}</label>
      {children}
      {hint && !error && <p className="field-hint">{hint}</p>}
      {error && <p className="field-err" role="alert">{error}</p>}
    </div>
  );
}

export const TextInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function TextInput({ className, ...rest }, ref) {
  return <input ref={ref} className={cx("input", className)} {...rest} />;
});

export const TextArea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function TextArea({ className, ...rest }, ref) {
  return <textarea ref={ref} className={cx("input", "textarea", className)} {...rest} />;
});

export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select className={cx("input", "select", className)} {...rest}>{children}</select>;
}

export function Toggle({ checked, onChange, label, hint, id }: { checked: boolean; onChange: (v: boolean) => void; label: ReactNode; hint?: ReactNode; id?: string }) {
  const auto = useId();
  const fid = id ?? auto;
  return (
    <label className="toggle" htmlFor={fid}>
      <span className="toggle-text"><span>{label}</span>{hint && <span className="field-hint">{hint}</span>}</span>
      <input id={fid} type="checkbox" role="switch" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="toggle-track" aria-hidden><span className="toggle-thumb" /></span>
    </label>
  );
}

export function Checkbox({ checked, onChange, label, className }: { checked: boolean; onChange: (v: boolean) => void; label: string; className?: string }) {
  return <input type="checkbox" className={cx("checkbox", className)} checked={checked} aria-label={label} onChange={(e) => onChange(e.target.checked)} onClick={(e) => e.stopPropagation()} />;
}

// ---------------------------------------------------------------- toasts

interface ToastItem { id: number; message: string; tone: Tone; action?: { label: string; run: () => void } }
let toasts: ToastItem[] = [];
const listeners = new Set<() => void>();
let nextToast = 1;
const emit = () => listeners.forEach((l) => l());

/** Show a short message. Returns its id so it can be dismissed early with dismissToast(). */
export function toast(message: string, opts: { tone?: Tone; action?: { label: string; run: () => void }; ms?: number } = {}): number {
  const item = { id: nextToast++, message, tone: opts.tone ?? "neutral", action: opts.action };
  toasts = [...toasts.slice(-2), item];
  emit();
  setTimeout(() => dismissToast(item.id), opts.ms ?? (opts.action ? 8000 : 4000));
  return item.id;
}

export function dismissToast(id: number): void {
  if (!toasts.some((x) => x.id === id)) return;
  toasts = toasts.filter((x) => x.id !== id);
  emit();
}

/**
 * Make a change that can be taken back. It shows at once (`apply`) and is only sent (`commit`)
 * after a short pause, unless the person taps Undo (`revert`). Use it for removals and rejections.
 */
export function undoable(message: string, opts: { apply?: () => void; commit: () => Promise<unknown> | void; revert?: () => void; ms?: number; tone?: Tone }): void {
  let undone = false;
  opts.apply?.();
  const ms = opts.ms ?? 6000;
  const id = toast(message, { tone: opts.tone, ms: ms + 500, action: { label: t("Undo"), run: () => { undone = true; opts.revert?.(); } } });
  setTimeout(() => {
    if (undone) return;
    dismissToast(id);
    Promise.resolve().then(opts.commit).catch((err: unknown) => {
      opts.revert?.();
      toast(err instanceof Error ? err.message : t("Something went wrong on our side. Try again."), { tone: "bad" });
    });
  }, ms);
}

export function Toaster() {
  const items = useSyncExternalStore((l) => { listeners.add(l); return () => listeners.delete(l); }, () => toasts);
  return (
    <div className="toaster" role="status" aria-live="polite">
      {items.map((x) => (
        <div key={x.id} className={cx("toast", `tone-${x.tone}`)}>
          <span className="toast-text">{x.message}</span>
          <span className="toast-actions">
            {x.action && <button type="button" onClick={() => { x.action!.run(); dismissToast(x.id); }}>{x.action.label}</button>}
            <button type="button" className="toast-x" aria-label={t("Dismiss")} onClick={() => dismissToast(x.id)}><Icon name="close" size={16} /></button>
          </span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- text

/** Minimal formatting for agent answers: paragraphs, "- " bullets and **bold**. No HTML. */
export function RichText({ text }: { text: string }) {
  const blocks = text.split(/\n{2,}/);
  const inline = (s: string, key: number) => s.split(/(\*\*[^*]+\*\*)/g).map((part, i) => part.startsWith("**") && part.endsWith("**") ? <strong key={`${key}-${i}`}>{part.slice(2, -2)}</strong> : <span key={`${key}-${i}`}>{part}</span>);
  return (
    <div className="rich">
      {blocks.map((block, bi) => {
        const lines = block.split("\n");
        if (lines.every((l) => /^\s*[-•*]\s+/.test(l) || !l.trim())) return <ul key={bi}>{lines.filter((l) => l.trim()).map((l, li) => <li key={li}>{inline(l.replace(/^\s*[-•*]\s+/, ""), li)}</li>)}</ul>;
        return <p key={bi}>{lines.map((l, li) => <span key={li}>{inline(l, li)}{li < lines.length - 1 && <br />}</span>)}</p>;
      })}
    </div>
  );
}

export function Avatar({ name, size = 32 }: { name: string; size?: number }) {
  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("");
  return <span className="avatar" style={{ width: size, height: size, fontSize: size * 0.4 }} aria-hidden>{initials}</span>;
}

// ---------------------------------------------------------------- small state helpers

/** Persist a small per-viewer preference (open tab, filter) in localStorage, safely. */
export function useLocalState<T>(key: string, initial: T): [T, (v: T) => void] {
  const [value, setValue] = useState<T>(() => {
    try { const raw = localStorage.getItem(`hondo.v2.${key}`); return raw ? JSON.parse(raw) as T : initial; } catch { return initial; }
  });
  const set = useCallback((v: T) => { setValue(v); try { localStorage.setItem(`hondo.v2.${key}`, JSON.stringify(v)); } catch { /* optional */ } }, [key]);
  return [value, set];
}

const MediaContext = createContext(false);
export const useIsWide = () => useContext(MediaContext);
export function MediaProvider({ children }: { children: ReactNode }) {
  const wide = useSyncExternalStore((l) => { const m = matchMedia("(min-width: 900px)"); m.addEventListener("change", l); return () => m.removeEventListener("change", l); }, () => matchMedia("(min-width: 900px)").matches, () => true);
  return <MediaContext.Provider value={wide}>{children}</MediaContext.Provider>;
}

// ---------------------------------------------------------------- small accessible pieces

/** Text for screen readers only. */
export function VisuallyHidden({ children, id }: { children: ReactNode; id?: string }) {
  return <span id={id} className="sr-only">{children}</span>;
}

/** A keyboard key, for shortcut hints. */
export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="kbd">{children}</kbd>;
}

type Theme = "light" | "dark";
interface Appearance { getTheme(): Theme; setTheme(theme: Theme): void; subscribe(listener: () => void): () => void }
const appearance = (): Appearance | undefined => (typeof window === "undefined" ? undefined : (window as unknown as { ranchAppearance?: Appearance }).ranchAppearance);
const subscribeTheme = (l: () => void) => appearance()?.subscribe(l) ?? (() => {});
const themeNow = (): Theme => appearance()?.getTheme() ?? (typeof document !== "undefined" && document.documentElement.dataset.theme === "dark" ? "dark" : "light");

/** The current light or dark appearance, kept in sync across tabs. */
export function useTheme(): Theme {
  return useSyncExternalStore(subscribeTheme, themeNow, () => "light");
}

/** The shared sun-and-moon switch from design.md: one control, saved on this browser. */
export function AppearanceToggle({ id }: { id?: string }) {
  const theme = useTheme();
  return (
    <button type="button" id={id} className="appearance-toggle" role="switch" aria-label={t("Dark mode")} aria-checked={theme === "dark"}
      title={theme === "dark" ? t("Switch to light mode") : t("Switch to dark mode")} onClick={() => appearance()?.setTheme(theme === "dark" ? "light" : "dark")}>
      <span className="appearance-track" aria-hidden>
        <span className="appearance-thumb" data-theme={theme} />
        <Icon name="today" size={14} className="appearance-sun" />
        <Icon name="moon" size={14} className="appearance-moon" />
      </span>
    </button>
  );
}

/** True when the person asked for less motion. */
export function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
}

export { cx };
